import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const SuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", payment_status: "missing" }); return; }
    let cancelled = false;
    const max = 12;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setState(data);
        setAttempts(n);
        if (data.payment_status === "paid") {
          if (!polled.current) {
            polled.current = true;
            clear();
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") return;
        if (n < max) setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setState({ status: "error", payment_status: "error" });
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  const paid = state.payment_status === "paid";
  const failed = state.payment_status === "failed" || state.status === "expired" || state.status === "error";

  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="border-2 border-zinc-800 bg-zinc-900/40 p-10 text-center" data-testid="success-card">
          {paid ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-lime-500 mx-auto mb-4" />
              <h1 className="font-display text-5xl text-zinc-50 uppercase">Payment Confirmed</h1>
              <p className="mt-4 text-zinc-400">Your order is locked in. Check your email for confirmation. Welcome to the road.</p>
              <div className="mt-6 font-mono-tech text-xs text-zinc-500 uppercase">
                AMOUNT // ${state.amount?.toFixed?.(2)} {state.currency?.toUpperCase()}<br />
                SESSION // {sessionId?.slice(0, 24)}…
              </div>
            </>
          ) : failed ? (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="font-display text-5xl text-zinc-50 uppercase">Payment Failed</h1>
              <p className="mt-4 text-zinc-400">Something went wrong. Your card was not charged. Try again or contact support.</p>
            </>
          ) : (
            <>
              <Loader2 className="w-16 h-16 text-zinc-200 mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-5xl text-zinc-50 uppercase">Confirming…</h1>
              <p className="mt-4 font-mono-tech text-xs text-zinc-500 uppercase">// Polling stripe // attempt {attempts}</p>
            </>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {paid && (
              <Link to="/track" className="bg-zinc-100 hover:bg-white text-zinc-950 px-6 py-3 font-display text-base tracking-widest uppercase" data-testid="success-track-order">Track your order</Link>
            )}
            <Link to="/shop" className="bg-transparent border border-zinc-100 hover:bg-zinc-100 hover:text-zinc-950 text-zinc-100 px-6 py-3 font-display text-base tracking-widest uppercase transition-colors" data-testid="continue-shopping">Continue Shopping</Link>
            <Link to="/" className="border border-zinc-700 hover:border-zinc-300 text-zinc-300 hover:text-zinc-100 px-6 py-3 font-display text-base tracking-widest uppercase transition-colors" data-testid="back-home">Home</Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SuccessPage;
